import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as employeeApi from "./api";
import type {
  EmployeeFilters,
  EmployeeFormValues,
  FamilyMemberFormValues,
} from "./types";

export function useEmployees(filters: EmployeeFilters = {}) {
  return useQuery({
    queryKey: ["employees", filters],
    queryFn: () => employeeApi.fetchEmployees(filters),
    placeholderData: (previous) => previous,
  });
}

export function useEmployee(id?: number) {
  return useQuery({
    queryKey: ["employees", "detail", id],
    queryFn: () => employeeApi.fetchEmployeeById(id as number),
    enabled: Number.isFinite(id) && Number(id) > 0,
  });
}

export function useDepartmentOptions() {
  return useQuery({
    queryKey: ["departments", "options"],
    queryFn: employeeApi.fetchDepartmentOptions,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmployeeOptions() {
  return useQuery({
    queryKey: ["employees", "options"],
    queryFn: employeeApi.fetchEmployeeOptions,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: EmployeeFormValues) =>
      employeeApi.createEmployee(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: EmployeeFormValues;
    }) => employeeApi.updateEmployee(id, payload),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({
          queryKey: ["employees", "detail", variables.id],
        }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => employeeApi.deleteEmployee(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

export function useDeactivateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => employeeApi.deactivateEmployee(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

export function useActivateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => employeeApi.activateEmployee(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

export function useImportEmployees() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => employeeApi.importEmployees(file),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["employees"] }),
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}
export function useRelationshipTypes() {
  return useQuery({
    queryKey: ["relationship-types"],
    queryFn: employeeApi.fetchRelationshipTypes,
    staleTime: 10 * 60 * 1000,
  });
}

export function useGenderOptions() {
  return useQuery({
    queryKey: ["gender-options"],
    queryFn: employeeApi.fetchGenderOptions,
    staleTime: 10 * 60 * 1000,
  });
}

export function useEmployeeFamily(employeeId?: number) {
  return useQuery({
    queryKey: ["employees", employeeId, "family"],
    queryFn: () => employeeApi.fetchEmployeeFamily(employeeId as number),
    enabled: Number.isFinite(employeeId) && Number(employeeId) > 0,
  });
}

export function useCreateFamilyMember(employeeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FamilyMemberFormValues) =>
      employeeApi.createFamilyMember(employeeId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["employees", employeeId, "family"],
      }),
  });
}

export function useUpdateFamilyMember(employeeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyId,
      payload,
    }: {
      familyId: number;
      payload: FamilyMemberFormValues;
    }) => employeeApi.updateFamilyMember(employeeId, familyId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["employees", employeeId, "family"],
      }),
  });
}

export function useDeleteFamilyMember(employeeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (familyId: number) =>
      employeeApi.deleteFamilyMember(employeeId, familyId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["employees", employeeId, "family"],
      }),
  });
}
export function useRoleOptions() {
  return useQuery({
    queryKey: ["roles", "options"],
    queryFn: employeeApi.fetchRoleOptions,
    staleTime: 5 * 60 * 1000,
  });
}
